import { useAccountState } from '../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate } from 'react-router';
import {
    Badge,
    Button,
    Divider,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    PositioningVirtualElement,
    Spinner,
    Text,
    Tooltip,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import {
    AddFilled,
    CalendarRegular,
    CheckmarkFilled,
    DismissFilled,
    EditRegular,
    MoneyOffRegular,
    MoneyRegular,
    MoreHorizontal20Regular,
    NoteRegular,
    PlayFilled,
    StopFilled,
} from '@fluentui/react-icons';
import { Column, Paths, Row } from '../common';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { Task, TimeTrackingEntry, UserAccount, UserRef } from '../../data/types';
import { useActiveTimeEntryService } from '../../data/services/useActiveTimeEntryService';
import { useAccess } from '../../auth/useAccess';
import { useState } from 'react';
import { useCaseServicesContext } from '../pages/Cases/Cases/CaseServicesContext';
import { useCaseService, useTimeTrackingService } from '../../data/services';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { appTokens } from '../common/Styles/appTokens';
import { AddEditTaskDialog } from '../dialogs/AddEditTaskDialog';
import { AddTimeEntryForTaskDialog } from '../dialogs/AddTimeEntryForTaskDialog';
import { LinkButton } from './LinkButton';
import { TimeTrackingTimerBarControl } from './TimeTrackingTimerBarControl';

const useStyles = makeStyles({
    taskText: {
        padding: '0px',
        overflow: 'hidden',
        width: 'inherit',
        display: 'block',
        overflowWrap: 'break-word',
        maxWidth: '100%',
        userSelect: 'none',
    },
    taskTextWordBreak: {
        wordBreak: 'break-all',
    },
    caseControl: {
        cursor: 'pointer',
        ':hover .fui-Persona__primaryText': {
            color: tokens.colorBrandStroke1,
        },
    },
    clickable: {
        cursor: 'pointer',
    },
});
export type TaskControlProps = {
    className?: string;
    task: Task;
    onComplete?: () => void;
    onClose?: () => void;
    onEdit?: (task: Task, cache?: boolean) => Promise<void>;
    acctUsers?: UserRef[];
    showCase?: boolean;
    showMilestone?: boolean;
    showAssigned?: boolean;
    showIfCaseDeleted?: boolean;
};
export const TaskControl = (props: TaskControlProps) => {
    const {
        className,
        task,
        onComplete,
        onClose,
        onEdit,
        showCase = true,
        showMilestone,
        showIfCaseDeleted = false,
    } = props;

    //Hooks
    const caseService = useCaseService();
    const { milestoneService } = useCaseServicesContext() ?? {};
    const navTo = useNavigate();
    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const { activeTimeEntry } = useActiveTimeEntryService();
    const timeEntryService = useTimeTrackingService();
    const access = useAccess(user);
    const mediaSizes = useMediaSizes();

    const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [contextMenuTarget, setContextMenuTarget] = useState<PositioningVirtualElement | null>(null);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const controlClasses = mergeClasses(commonStyles.utility.withHiddenChildren, styles.clickable, className);
    const isOverdue = () => {
        return new Date(task?.dueBy as Date) < new Date();
    };

    //Logic
    const getCase = () => caseService?.findCaseById(task?.caseId);
    const getMilestone = () => milestoneService?.findMilestone(task?.milestoneId);
    const canCloseTask = () => {
        return access.cases.canEditCaseAsAdmin(task?.caseId) || user.userId === task?.userId;
    };
    const canCompleteTask = () => {
        return user.userId === task?.userId;
    };
    const epoch = new Date('1970-01-01T00:00:00Z');
    const hasCompleteBy = () => {
        const compDate = new Date(task?.dueBy as Date);
        return !!task?.dueBy && compDate > epoch;
    };
    const getCompleteBy = () => {
        if (!!hasCompleteBy()) {
            const compByDate = new Date(`${task?.dueBy as Date}`);
            return `${compByDate.toLocaleDateString()}`;
        }
    };
    const isActiveTimeEntry = () => {
        return activeTimeEntry?.taskId == task.taskId;
    };
    const shouldShowControl = () => {
        const theCase = getCase();
        return !!theCase || (!theCase && !!showIfCaseDeleted);
    };
    const onClickStartTimer = async (e: any) => {
        e.stopPropagation();

        await timeEntryService.startTimerAsync(task);
    };
    const onClickStopTimer = async (e: any) => {
        e.stopPropagation();

        await timeEntryService.stopTimerAsync(task);
    };
    const submitTimeFromTask = async (entry: TimeTrackingEntry) => {
        if (!!entry) {
            await timeEntryService.addOrMergeTimeEntryAsync(entry);
        }
    };

    //UI Events
    const onClickCase = (e: any, caseId?: string) => {
        e.stopPropagation();

        if (caseId) {
            navTo(Paths.viewEditCase(caseId));
        }
    };
    const onDoubleClickCard = (e: any) => {
        e.stopPropagation();

        setIsEditOpen(true);
    };
    const onContextMenuCard = (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        const { clientX, clientY } = e;
        // Anchor the menu to the pointer location instead of the trigger button.
        setContextMenuTarget({ getBoundingClientRect: () => new DOMRect(clientX, clientY, 0, 0) });
        setIsMenuOpen(true);
    };
    const onMenuOpenChange = (isOpen: boolean) => {
        setIsMenuOpen(isOpen);

        if (!isOpen) {
            setContextMenuTarget(null);
        }
    };

    return (
        <>
            {shouldShowControl() && (
                <>
                    <Column
                        shadowSmall
                        className={controlClasses}
                        subtleBorder
                        padding='pl-2 pr-3 py-2'
                        height='fit-content'
                        onDoubleClick={onDoubleClickCard}
                        onContextMenu={onContextMenuCard}
                        borderLeftStyle={appTokens.taskControl.borderLeftStyle}
                        borderRadius={appTokens.taskControl.borderRadius}
                        backgroundColor={appTokens.taskControl.background}
                        noGap
                    >
                        <Row noWrap noGap>
                            <Column flex='1' vAlign='middle'>
                                <Text size={200} weight='regular'>
                                    TASK
                                </Text>
                            </Column>
                            <Column flex='1' align='right' vAlign='middle'>
                                <TaskControlMenu
                                    activeTimeEntry={activeTimeEntry}
                                    task={task}
                                    open={isMenuOpen}
                                    onOpenChange={onMenuOpenChange}
                                    positioningTarget={contextMenuTarget}
                                    onStartTimer={onClickStartTimer}
                                    onStopTimer={onClickStopTimer}
                                    onEdit={() => setIsEditOpen(true)}
                                    canCloseTask={canCloseTask()}
                                    onClose={onClose}
                                    canCompleteTask={canCompleteTask()}
                                    onComplete={onComplete}
                                    submitTimeFromTask={submitTimeFromTask}
                                />
                            </Column>
                        </Row>

                        <Column noGap flex='1 1 auto' vAlign='middle' align='left' overflowX='hidden' padding='pb-2'>
                            <Tooltip
                                content={
                                    <Text weight='semibold'>
                                        Description:{' '}
                                        <Text>
                                            {task.text?.substring(0, 150)}
                                            {task.text && task.text.length > 150 ? '...' : ''}
                                        </Text>
                                    </Text>
                                }
                                relationship='label'
                            >
                                <Text size={mediaSizes.isMobile ? 400 : 500} weight='semibold'>
                                    {task.title}
                                </Text>
                            </Tooltip>
                            {!!showCase && (
                                <Row gap='small' noWrap>
                                    <Column vAlign='middle' padding='pt-0' width='auto'>
                                        <Text weight='semibold' size={200}>
                                            Case:
                                        </Text>
                                    </Column>
                                    <Column vAlign='top' align='left' minWidth='0px'>
                                        <LinkButton
                                            onClick={(e: any) => onClickCase(e, task?.caseId as string)}
                                            text={getCase()?.caseName || ''}
                                            data-testid={`task-control-case-link-${task.caseId}`}
                                        />
                                    </Column>
                                </Row>
                            )}
                            {!!showMilestone && getMilestone() && (
                                <Row gap='small' noWrap>
                                    <Column vAlign='middle' padding='pt-0' width='auto'>
                                        <Text weight='semibold' size={200}>
                                            Milestone:
                                        </Text>
                                    </Column>
                                    <Column vAlign='top' align='left'>
                                        <Text weight='semibold'>
                                            <Text>{getMilestone()?.name}</Text>
                                        </Text>
                                    </Column>
                                </Row>
                            )}
                        </Column>

                        <Row gap='medium' padding='py-1' minHeight='4rem'>
                            <Column flex='1 0 250px' >
                                <Text weight='semibold'>
                                    Description:{'  '}
                                    <Text weight='regular'>
                                        {task?.text?.substring(0, 150)}
                                        {task?.text && task?.text.length > 150 ? '...' : ''}
                                    </Text>
                                </Text>
                            </Column>
                            <Column flex='0 0 auto' align='right' noGap padding='py-1' >
                                <Column flex='1' className={!isActiveTimeEntry() && !mediaSizes.isUnderLaptop ? 'hidden' : ''}>
                                    <TimeTrackingTimerBarControl task={task} size='small' />
                                </Column>
                            </Column>
                        </Row>

                        <Column flex='1' align='left' vAlign='bottom'>
                            <Row width='auto' gap='medium'>
                                {!!task?.billable ? (
                                    <>
                                        <Badge
                                            appearance='tint'
                                            color='success'
                                            size='small'
                                            icon={<MoneyRegular />}
                                            style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}
                                        >
                                            BILLABLE
                                        </Badge>
                                    </>
                                ) : (
                                    <>
                                        <Badge
                                            appearance='tint'
                                            color='brand'
                                            size='small'
                                            icon={<MoneyOffRegular />}
                                            style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}
                                        >
                                            Non-Billable
                                        </Badge>
                                    </>
                                )}
                                {hasCompleteBy() && (
                                    <Badge
                                        appearance={isOverdue() ? 'filled' : 'tint'}
                                        color={isOverdue() ? 'danger' : 'brand'}
                                        size='small'
                                        icon={<CalendarRegular />}
                                        style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}
                                    >
                                        {isOverdue() && 'Overdue -'} Due by: {getCompleteBy()}
                                    </Badge>
                                )}
                                {task?.hasNotes && (
                                    <Badge
                                        appearance='tint'
                                        color='informative'
                                        size='small'
                                        icon={<NoteRegular />}
                                        style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}
                                    >
                                        Notes
                                    </Badge>
                                )}
                            </Row>
                        </Column>
                    </Column>

                    {!!onEdit && (
                        <AddEditTaskDialog
                            onSubmit={onEdit}
                            initialCaseId={task.caseId}
                            taskToEdit={task}
                            open={isEditOpen}
                            onOpenChange={(isOpen) => setIsEditOpen(isOpen)}
                        />
                    )}
                </>
            )}
        </>
    );
};

interface TaskControlMenuProps {
    task: Task;
    activeTimeEntry?: TimeTrackingEntry;
    onStartTimer?: (e: any) => Promise<void>;
    onStopTimer?: (e: any) => Promise<void>;
    submitTimeFromTask?: (timeEntry: TimeTrackingEntry) => Promise<void>;
    onEdit?: () => void;
    onComplete?: () => void;
    onClose?: () => void;
    canCloseTask?: boolean;
    canCompleteTask?: boolean;
    className?: string;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    positioningTarget?: PositioningVirtualElement | null;
}
const TaskControlMenu = ({
    task,
    activeTimeEntry,
    open,
    onOpenChange,
    positioningTarget,
    onStartTimer,
    onStopTimer,
    submitTimeFromTask,
    onEdit,
    onComplete,
    onClose,
    canCloseTask,
    canCompleteTask,
    className,
}: TaskControlMenuProps) => {
    const isActiveTimeEntry = () => {
        return activeTimeEntry?.taskId == task.taskId;
    };

    const [isAddTimeOpen, setIsAddTimeOpen] = useState<boolean>(false);

    return (
        <>
            <Menu
                open={open}
                onOpenChange={(_, data) => onOpenChange?.(data.open)}
                positioning={positioningTarget ? { target: positioningTarget } : undefined}
            >
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        icon={<MoreHorizontal20Regular />}
                        size='small'
                        appearance='secondary'
                        className={className}
                        aria-label='Actions'
                        onClick={(e: any) => e.stopPropagation()}
                        data-testid='task-actions-menu-trigger'
                    />
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        {!!onStartTimer && (
                            <MenuItem
                                icon={
                                    isActiveTimeEntry() ? (
                                        <Spinner size='extra-tiny' />
                                    ) : (
                                        <PlayFilled
                                            color={
                                                !!activeTimeEntry
                                                    ? tokens.colorNeutralForegroundDisabled
                                                    : tokens.colorStatusSuccessForeground1
                                            }
                                        />
                                    )
                                }
                                disabled={!!activeTimeEntry}
                                onClick={onStartTimer}
                            >
                                Start Time
                            </MenuItem>
                        )}

                        {!!onStopTimer && (
                            <MenuItem
                                icon={
                                    <StopFilled
                                        color={
                                            !isActiveTimeEntry()
                                                ? tokens.colorNeutralForegroundDisabled
                                                : tokens.colorStatusDangerForeground1
                                        }
                                    />
                                }
                                disabled={!isActiveTimeEntry()}
                                onClick={onStopTimer}
                            >
                                Stop Time
                            </MenuItem>
                        )}

                        {!!task && !isActiveTimeEntry() && (
                            <MenuItem icon={<AddFilled />} onClick={() => setIsAddTimeOpen(true)}>
                                Add Time
                            </MenuItem>
                        )}

                        <Divider />

                        {onEdit && task && (
                            <MenuItem icon={<EditRegular />} onClick={onEdit}>
                                Edit Task
                            </MenuItem>
                        )}

                        {canCloseTask && onClose && (
                            <MenuItem
                                icon={<CheckmarkFilled color={tokens.colorStatusSuccessForeground1} />}
                                onClick={onComplete}
                            >
                                Complete Task
                            </MenuItem>
                        )}

                        {canCompleteTask && onComplete && (
                            <MenuItem icon={<DismissFilled color={tokens.colorStatusDangerForeground1} />} onClick={onClose}>
                                Close Task
                            </MenuItem>
                        )}
                    </MenuList>
                </MenuPopover>
            </Menu>

            {/* Rendered outside the Menu so closing the menu popover doesn't unmount the dialog. */}
            {!!task && (
                <AddTimeEntryForTaskDialog
                    caseId={task.caseId}
                    task={task}
                    onSubmit={submitTimeFromTask}
                    open={isAddTimeOpen}
                    onOpenChange={setIsAddTimeOpen}
                />
            )}
        </>
    );
};
